import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  AttachFile,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  CloudUpload,
  ContentPaste,
} from '@mui/icons-material';
import { appStore } from '../stores/AppStore';
import DeviceCard from './DeviceCard';
import API_CONFIG from '../config/api.config';
import { trackButtonClick, trackMessageAction, trackExternalMessage, trackDeviceAction } from '../utils/analytics';

interface SendPageProps {
  onBack: () => void;
  onExternalMessage?: () => void;
}

const SendPage: React.FC<SendPageProps> = observer(({ onBack, onExternalMessage }) => {
  const [contentType, setContentType] = useState<'text' | 'files'>('text');

  const handleDeviceSelect = (device: any) => {
    trackButtonClick('Select Device', 'Send Page');
    trackDeviceAction('select', device.deviceType);
    appStore.setSelectedDevice(device);
  };

  const handleSendClick = () => {
    trackButtonClick('Send Message', 'Send Page');
    trackMessageAction('send', appStore.selectedDevice?.deviceType);
    appStore.sendMessage();
    // Reset after sending
    setTimeout(() => {
      onBack();
    }, 500);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    trackButtonClick('Select Files', 'Send Page');
    trackMessageAction('select_files', `${files.length} files`);

    try {
      await appStore.addFiles(Array.from(files));
    } catch (err: any) {
      appStore.setError(err.message || 'Failed to upload files');
    }

    // Reset input to allow selecting the same files again
    event.target.value = '';
  };

  const handlePasteFromClipboard = async () => {
    trackButtonClick('Paste from Clipboard', 'Send Page');
    try {
      if (!navigator.clipboard) {
        appStore.setError('Clipboard API not available');
        return;
      }

      // Try to read clipboard items (for images)
      const clipboardItems = await navigator.clipboard.read();
      
      for (const item of clipboardItems) {
        const imageTypes = item.types.filter(type => type.startsWith('image/'));
        
        if (imageTypes.length > 0) {
          const blob = await item.getType(imageTypes[0]);
          const file = new File([blob], 'clipboard-image.png', { type: blob.type });
          await appStore.addFiles([file]);
          return;
        }
      }
      
      // If no image, try to read text
      const text = await navigator.clipboard.readText();
      if (text && text.trim()) {
        appStore.setMessageText(text);
      }
    } catch (err: any) {
      appStore.setError(err.message || 'Failed to read clipboard');
    }
  };

  const removeFile = (index: number) => {
    trackButtonClick('Remove File', 'Send Page');
    appStore.removeFile(index);
  };

  const clearAll = () => {
    trackButtonClick('Clear All', 'Send Page');
    appStore.clearFileInput();
    appStore.setMessageText('');
  };

  // If no device selected, show device selection
  if (!appStore.selectedDevice) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          py: 2,
          px: 2,
        }}
      >
        <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(10px)', maxWidth: 600, width: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                Select Device to Send
              </Typography>
            </Box>

            {/* External Message Button - at the top */}
            {onExternalMessage && (
              <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    trackButtonClick('Send to not my device', 'Send Page');
                    trackExternalMessage('initiate', 'send');
                    onExternalMessage();
                  }}
                  sx={{
                    color: '#FFFFFF',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.7)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Send to not my device
                </Button>
              </Box>
            )}

            {/* My devices title */}
            {appStore.otherDevices.length > 0 && (
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#FFFFFF', 
                  fontWeight: 'bold', 
                  mb: 1.5,
                  mt: appStore.otherDevices.length > 0 ? 0 : 0
                }}
              >
                My devices
              </Typography>
            )}

            {appStore.isLoadingDevices ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : appStore.otherDevices.length === 0 ? (
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', py: 4 }}>
                No other devices available
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {appStore.otherDevices.map((device) => (
                  <DeviceCard
                    key={device.id}
                    device={device}
                    isSelected={appStore.selectedDevice?.id === device.id}
                    onClick={() => handleDeviceSelect(device)}
                    isCurrentDevice={false}
                  />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Show message composer when device is selected
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        py: 2,
        px: 1,
      }}
    >
      <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(10px)', maxWidth: 800, width: '100%' }}>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
              New Message
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              To: {appStore.selectedDevice.name}
            </Typography>
          </Box>

          {/* Content Type Selection */}
          <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1, fontWeight: 500 }}>
            Choose content type:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Chip
              label="Text"
              onClick={() => {
                trackButtonClick('Content Type: Text', 'Send Page');
                setContentType('text');
                appStore.clearFileInput();
              }}
              sx={{
                backgroundColor: contentType === 'text' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                color: '#FFFFFF',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: contentType === 'text' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
                },
              }}
            />
            <Chip
              label="Files"
              onClick={() => {
                trackButtonClick('Content Type: Files', 'Send Page');
                setContentType('files');
                appStore.setMessageText('');
              }}
              sx={{
                backgroundColor: contentType === 'files' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                color: '#FFFFFF',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: contentType === 'files' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
                },
              }}
            />
          </Box>

          {/* Text Input */}
          {contentType === 'text' && (
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Type your message or paste from clipboard..."
                value={appStore.messageText}
                onChange={(e) => appStore.setMessageText(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<ContentPaste />}
                  onClick={handlePasteFromClipboard}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: '#FFFFFF',
                    '&:hover': {
                      borderColor: '#FFFFFF',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Paste
                </Button>
              </Box>
            </Box>
          )}

          {/* Files Input */}
          {contentType === 'files' && (
            <Box sx={{ mb: 1.5 }}>
              {/* File Selection Buttons */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-upload"
                  accept="*/*"
                />
                <Button
                  variant="outlined"
                  component="label"
                  htmlFor="file-upload"
                  startIcon={<AttachFile />}
                  disabled={appStore.isUploadingFiles}
                  onClick={() => trackButtonClick('Select Files', 'Send Page')}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: '#FFFFFF',
                    '&:hover': {
                      borderColor: '#FFFFFF',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Select Files
                </Button>
              </Box>

              {/* Selected Files Preview */}
              {appStore.selectedFiles.length > 0 && (
                <Box sx={{ mb: 1, p: 1, border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 1, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ color: '#FFFFFF' }}>
                      {appStore.selectedFiles.length} file(s) selected
                      {appStore.isUploadingFiles && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: '#2196F3' }}>
                          (Uploading...)
                        </Typography>
                      )}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={clearAll}
                      disabled={appStore.isUploadingFiles}
                      sx={{ color: '#FFFFFF' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                    {appStore.sessionTotalSize > 0 && (
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', display: 'block', mb: 0.5 }}>
                        Total uploaded size: {appStore.formatFileSize(appStore.sessionTotalSize)}
                      </Typography>
                    )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {appStore.selectedFiles.map((file, index) => {
                      const uploadStatus = appStore.fileUploadStatus.get(index);
                      const isUploading = uploadStatus === 'uploading';
                      const isUploaded = uploadStatus === 'uploaded';
                      const hasError = uploadStatus === 'error';

                      return (
                        <Box
                          key={index}
                          sx={{
                            position: 'relative',
                            border: hasError ? '2px solid #f44336' : '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 1,
                            p: 0.5,
                            minWidth: 100,
                            maxWidth: 150,
                            opacity: isUploading ? 0.7 : 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          }}
                        >
                          {file.type.startsWith('image/') && appStore.filePreviews.has(index) ? (
                            <>
                              <img
                                src={appStore.filePreviews.get(index)}
                                alt={file.name}
                                style={{
                                  width: '100%',
                                  maxHeight: 100,
                                  objectFit: 'contain',
                                  borderRadius: 4,
                                }}
                              />
                              <Box sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                display: 'flex',
                                gap: 0.25,
                                alignItems: 'center',
                              }}>
                                {isUploading && <CircularProgress size={16} sx={{ color: '#2196F3' }} />}
                                {isUploaded && <CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />}
                                {hasError && <ErrorIcon sx={{ fontSize: 16, color: '#f44336' }} />}
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile(index)}
                                  disabled={isUploading}
                                  sx={{ bgcolor: 'rgba(255,255,255,0.8)', color: '#000' }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </>
                          ) : (
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                <AttachFile sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.8)' }} />
                                <Typography variant="caption" sx={{ color: '#FFFFFF', wordBreak: 'break-word', flexGrow: 1 }}>
                                  {file.name}
                                </Typography>
                                {isUploading && <CircularProgress size={16} sx={{ color: '#2196F3' }} />}
                                {isUploaded && <CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />}
                                {hasError && <ErrorIcon sx={{ fontSize: 16, color: '#f44336' }} />}
                              </Box>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                {appStore.formatFileSize(file.size)}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile(index)}
                                disabled={isUploading}
                                sx={{ mt: 0.25, color: '#FFFFFF' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
            <Button
              variant="outlined"
              onClick={() => {
                trackButtonClick('Back', 'Send Page');
                onBack();
              }}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: '#FFFFFF',
                '&:hover': {
                  borderColor: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={appStore.isSending || appStore.isUploadingFiles ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : <Send />}
              onClick={handleSendClick}
              disabled={!appStore.canSendMessage || appStore.isSending || appStore.isUploadingFiles}
              sx={{
                backgroundColor: '#2196F3',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#1976D2',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {appStore.isUploadingFiles ? 'Uploading...' : appStore.isSending ? 'Sending...' : 'Send'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

export default SendPage;

